/**
 * IndexShowcase — fw-choi UI 컴포넌트 쇼케이스(데모). 실제 업무 화면 아님.
 * 【역할】 공통 컴포넌트/패턴의 사용 예시를 한 화면에 모아 보여준다.
 */
import { Card, Typography } from 'antd'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'

const { Title, Text } = Typography
import {
  EditOutlined,
  ThunderboltOutlined,
  TableOutlined,
  AppstoreOutlined,
  CopyOutlined,
  BarsOutlined,
  BellOutlined,
  PictureOutlined,
  UploadOutlined,
  BarChartOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

/** 쇼케이스 카탈로그 카드 1건 메타데이터. */
interface Category {
  /** 카드 표시 이름 (i18n 라벨 또는 고정 영문). */
  name: string
  /** 클릭 시 이동할 라우터 path (e.g. '/showcase/form'). */
  path: string
  /** 좌측 아이콘 ReactNode (@ant-design/icons). */
  icon: ReactNode
  /** 카드 본문 한 줄 요약 (포함 컴포넌트 나열). */
  desc: string
}

/**
 * ============================================================================
 * IndexShowcase — antd 컴포넌트 쇼케이스 카탈로그 진입점
 * ============================================================================
 *
 * 【역할】 12개 Showcase 화면을 그리드 카드로 노출하는 네비게이션 허브.
 *         "어떤 antd 컴포넌트가 있고 어떻게 쓰이는지" 한 눈에 탐색.
 *
 * 【시연 항목】
 *   - Form 입력 / Button / Data 표시 / Panel 레이아웃 / Overlay 팝업
 *   - Menu 내비게이션 / Message 알림 / Media & Misc / File 업로드
 *   - Chart / Editor (총 11 카드 + 자체 1)
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase' }` (lazy)
 * 【포팅 원본】 vue-spring-fw `ShowcaseIndex.vue` 의 React/antd 포팅
 * 【의존성】 antd Card/Typography, @ant-design/icons, react-router-dom
 * 【참고】 https://ant.design/components/overview
 *         실 도메인 CRUD 패턴은 `/scaffold` (Wave 16 카탈로그) 참조.
 */
export default function IndexShowcase() {
  const navigate = useNavigate()
  const { t } = useLabel()

  const categories: Category[] = [
    {
      name: t('LBL_SC_CAT_FORM', 'Form 입력'),
      path: '/showcase/form',
      icon: <EditOutlined />,
      desc: 'Input, Select, DatePicker, Checkbox, Radio, Switch, Slider'
    },
    {
      name: 'Button',
      path: '/showcase/button',
      icon: <ThunderboltOutlined />,
      desc: 'Button, Dropdown, FloatButton'
    },
    {
      name: t('LBL_SC_CAT_DATA', 'Data 표시'),
      path: '/showcase/data',
      icon: <TableOutlined />,
      desc: 'Table, List, Pagination, Timeline, Tree, Descriptions'
    },
    {
      name: t('LBL_SC_CAT_PANEL', 'Panel 레이아웃'),
      path: '/showcase/panel',
      icon: <AppstoreOutlined />,
      desc: 'Collapse, Card, Divider, Splitter, Steps, Tabs'
    },
    {
      name: t('LBL_SC_CAT_OVERLAY', 'Overlay 팝업'),
      path: '/showcase/overlay',
      icon: <CopyOutlined />,
      desc: 'Modal, Drawer, Popover, Tooltip, Modal.confirm'
    },
    {
      name: t('LBL_SC_CAT_MENU', 'Menu 내비게이션'),
      path: '/showcase/menu',
      icon: <BarsOutlined />,
      desc: 'Breadcrumb, Menu, Dropdown, Steps'
    },
    {
      name: t('LBL_SC_CAT_MESSAGE', 'Message 알림'),
      path: '/showcase/message',
      icon: <BellOutlined />,
      desc: 'message, notification, Alert'
    },
    {
      name: 'Media & Misc',
      path: '/showcase/misc',
      icon: <PictureOutlined />,
      desc: 'Avatar, Badge, Tag, Progress, Carousel, FloatButton'
    },
    {
      name: t('LBL_SC_CAT_FILE', 'File 업로드'),
      path: '/showcase/file',
      icon: <UploadOutlined />,
      desc: t('LBL_SC_CAT_FILE_DESC', 'Upload (기본, 드래그, 다중)')
    },
    {
      name: 'Chart',
      path: '/showcase/chart',
      icon: <BarChartOutlined />,
      desc: 'echarts-for-react — Line, Bar, Pie, Radar'
    },
    {
      name: 'Editor',
      path: '/showcase/editor',
      icon: <FileTextOutlined />,
      desc: 'Rich text editor'
    }
  ]

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_COMPONENT_SHOWCASE', '컴포넌트 쇼케이스')}
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('LBL_COMPONENT_SHOWCASE_DESC', 'Ant Design v5 컴포넌트 데모')}
        </Text>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridAutoRows: '1fr',
          gap: 10
        }}
      >
        {categories.map((cat) => (
          <Card
            key={cat.path}
            hoverable
            size="small"
            onClick={() => navigate(cat.path)}
            styles={{ body: { padding: 10, height: '100%' } }}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 18, color: '#1677ff' }}>{cat.icon}</span>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{cat.name}</h3>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: '#666', lineHeight: 1.4 }}>{cat.desc}</p>
          </Card>
        ))}
      </div>
    </PageContainer>
  )
}
